/**
 * The production wizard's steps, as one list.
 *
 * The editor (apps/web CreateEditBriefing) owns the screens; `briefings
 * .last_edit_step` stores which one a producer stopped on. The admin panel has
 * to turn that bare number back into "step 4 of 9 — The crew", so the labels
 * can no longer live inside the editor file: two copies would drift the moment
 * a step is inserted, and the admin panel would silently name the wrong screen.
 *
 * The index is load-bearing elsewhere too: `apps/web/src/lib/saveScope.ts`
 * maps the same numbers to per-step save scopes. Inserting a step means
 * editing that table as well.
 */

/** Wizard screens in order. The index IS the stored `last_edit_step` value. */
export const PRODUCTION_STEPS = [
  "Basic information",
  "Stages per day",
  "Timetable import",
  "The crew",
  "Festival map",
  "Crew schedules",
  "General briefing",
  "Live tracking setup",
  "Review & publish",
] as const;

export interface ProductionProgress {
  /** 1-based step the producer is on, clamped into the real range. */
  stepNumber: number;
  /** Total number of wizard steps. */
  stepCount: number;
  /** Human label of that step. */
  stepLabel: string;
  /** True once the production is published — the wizard is behind them. */
  isPublished: boolean;
  /** 0–100, how far through the wizard. Published always reads 100. */
  percentComplete: number;
}

/**
 * Where a producer stands in a production.
 *
 * A published production reports the final step regardless of the stored
 * number: `last_edit_step` keeps whatever screen was open when it was saved,
 * so a published briefing can still carry a low number and would otherwise be
 * shown as unfinished.
 */
export function describeProductionProgress(
  status: string | null | undefined,
  lastEditStep: number | null | undefined,
): ProductionProgress {
  const stepCount = PRODUCTION_STEPS.length;
  const isPublished = status === "published";

  if (isPublished) {
    return {
      stepNumber: stepCount,
      stepCount,
      stepLabel: PRODUCTION_STEPS[stepCount - 1],
      isPublished: true,
      percentComplete: 100,
    };
  }

  const raw = typeof lastEditStep === "number" && Number.isFinite(lastEditStep) ? lastEditStep : 0;
  const index = Math.min(Math.max(Math.trunc(raw), 0), stepCount - 1);

  return {
    stepNumber: index + 1,
    stepCount,
    stepLabel: PRODUCTION_STEPS[index],
    isPublished: false,
    percentComplete: Math.round((index / (stepCount - 1)) * 100),
  };
}
